import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import { translateOutcomeTreeData } from "../../RuleTree/treeUtils";
import { useHistory } from "react-router-dom";
import axios from "axios"
import { CircularProgress } from '@material-ui/core';
import { decisionReducer } from "../../../Utils/requestUtils"
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from '@material-ui/icons/Clear';
import TextField from '@material-ui/core/TextField';
import { useTranslation } from "react-i18next";
import AuthContext from "../../../../context/auth-context";
import RulesContext from "../../../../context/rules-context";
import CollapsibleMarkdown from "../../RuleTree/CollapsibleMarkdown";
import QiaAvatar from "../../RuleTree/QiaAvatar";
import QiaLoader from "../../RuleTree/QiaLoader";
import "../../RuleTree/ruleAgentTheme.css";
import {
    buildCompanyEventPayload,
    buildNewRulePrompt,
    buildRuleChangePrompt,
    cancelRuleAgentTurn,
    extractRuleChangeSummary,
    PREVIOUS_RULE_ATTACHMENT_LABEL,
    getRuleAgentDisplayText,
    openRuleAgentSession,
    streamRuleChangeTurn
} from "../../RuleTree/ruleAgentClient";

const getConfirmationIcon = (success) => {
    if (success) {
        return (<CheckIcon className="fraudStatusIcon" style={{ color: '#73C580', width: "40px", height: "40px" }} />)
    }
    else {
        return (<ClearIcon className="fraudStatusIcon" style={{ color: '#D91A1A', width: "40px", height: "40px" }} />)
    }
}

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

function SaveRulePieceDialog(props) {
    const { t } = useTranslation();
    let { rulePieceDefinition, userVariablesAvailable } = props

    const history = useHistory()
    const { keycloak_adapter, user_data } = useContext(AuthContext)
    const rulesContext = useContext(RulesContext)
    let hasAgentRole = user_data.roles.includes("rule_engine_agent_user")
    let agentEnabled = hasAgentRole
    let previousTreeData = rulePieceDefinition.tree_data || []
    let isFirstVersion = previousTreeData.length === 0

    const [ruleDescription, setRuleDescription] = useState("")

    const [dialogState, dispatchDialogState] = useReducer(
        decisionReducer,
        { isLoading: false, isError: false, finished: false }
    )

    const [agentExplanationState, setAgentExplanationState] = useState({status: "idle", text: "", errorMessage: null})
    const [agentWarnings, setAgentWarnings] = useState([])
    const [showCriticalConfirm, setShowCriticalConfirm] = useState(false)
    const fullAgentTextRef = useRef("")
    const abortControllerRef = useRef(null)
    const agentSessionIdRef = useRef(null)

    let criticalWarnings = agentWarnings.filter((warning) => warning.severity === "critical")

    const abortAgentFlow = () => {
        let previousController = abortControllerRef.current
        if (previousController && !previousController.signal.aborted) {
            previousController.abort()
            let token = keycloak_adapter && keycloak_adapter.token
            if (agentSessionIdRef.current && token) {
                cancelRuleAgentTurn({ sessionId: agentSessionIdRef.current, token })
            }
        }
    }

    const runAgentExplanationFlow = () => {
        if (!agentEnabled) return

        abortAgentFlow()
        let abortController = new AbortController()
        abortControllerRef.current = abortController
        fullAgentTextRef.current = ""
        setAgentExplanationState({status: "loading", text: "", errorMessage: null})
        setAgentWarnings([])
        setShowCriticalConfirm(false)

        let token = keycloak_adapter && keycloak_adapter.token
        if (!token) {
            setAgentExplanationState({status: "error", text: "", errorMessage: "missing_token"})
            return
        }

        let prompt
        let attachments = null
        let ruleContext = {
            tree_data: translateOutcomeTreeData(props.treeData),
            user_variables: userVariablesAvailable || [],
            name: rulePieceDefinition.name,
            is_rule_piece: true
        }
        let companyEvent = buildCompanyEventPayload(rulesContext)
        if (isFirstVersion) {
            prompt = buildNewRulePrompt({isRulePiece: true})
        } else {
            prompt = buildRuleChangePrompt({isRulePiece: true})
            attachments = [{
                label: PREVIOUS_RULE_ATTACHMENT_LABEL,
                tree_data: previousTreeData
            }]
        }

        openRuleAgentSession({token, signal: abortController.signal})
            .then((sessionId) => {
                if (abortController.signal.aborted) return
                agentSessionIdRef.current = sessionId
                return streamRuleChangeTurn({
                    sessionId,
                    token,
                    prompt,
                    ruleContext,
                    companyEvent,
                    attachments,
                    signal: abortController.signal,
                    onDelta: (data) => {
                        if (abortController.signal.aborted) return
                        fullAgentTextRef.current += data.text || ""
                        setAgentExplanationState({
                            status: "streaming",
                            text: getRuleAgentDisplayText(fullAgentTextRef.current),
                            errorMessage: null
                        })
                    },
                    onWarnings: (data) => {
                        if (abortController.signal.aborted) return
                        let newWarnings = (data || {}).warnings || []
                        if (newWarnings.length > 0) {
                            setAgentWarnings((previous) => previous.concat(newWarnings))
                        }
                    },
                    onDone: () => {
                        if (abortController.signal.aborted) return
                        let summary = extractRuleChangeSummary(fullAgentTextRef.current)
                        if (summary) setRuleDescription(summary)
                        let displayText = getRuleAgentDisplayText(fullAgentTextRef.current)
                        let hasSubstance = displayText.replace(/[-_*#\s]/g, "").length > 0
                        if (!hasSubstance) {
                            setAgentExplanationState({status: "error", text: "", errorMessage: "empty_analysis"})
                            return
                        }
                        setAgentExplanationState({
                            status: "success",
                            text: displayText,
                            errorMessage: null
                        })
                    },
                    onError: (data) => {
                        if (abortController.signal.aborted) return
                        setAgentExplanationState({status: "error", text: "", errorMessage: (data || {}).message})
                    }
                })
            })
            .catch((error) => {
                if (abortController.signal.aborted) return
                setAgentExplanationState({status: "error", text: "", errorMessage: error.message})
            })
    }

    useEffect(() => {
        if (props.open && agentEnabled) {
            runAgentExplanationFlow()
        } else {
            fullAgentTextRef.current = ""
            setAgentExplanationState({status: "idle", text: "", errorMessage: null})
            setAgentWarnings([])
            setShowCriticalConfirm(false)
        }
        return () => {
            abortAgentFlow()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open])

    const handleCloseDialog = () => {
        props.onClose()
        setTimeout(function () { dispatchDialogState({ type: "default_state" }) }, 500)
    }

    const handleFinish = () => {
        dispatchDialogState({ type: "send_request_init" })
        let finalTreeData = translateOutcomeTreeData(props.treeData)
        let payload = {
            name: rulePieceDefinition.name,
            description: ruleDescription,
            tree_data: finalTreeData,
            user_variables: userVariablesAvailable
        }
        setTimeout(function () {
            let url = `/dash/rule_engine/rule_piece/${rulePieceDefinition.rule_piece_key}?environment=${rulePieceDefinition.environment}`
            axios.put(url, payload, {}).then(response => {
                dispatchDialogState({ type: "send_request_success" })
            }).catch(error => {
                if ((error.response || {}).status === 403) dispatchDialogState({ type: "send_request_failure_403" })
                else if ((error.response || {}).status === 409) dispatchDialogState({ type: "send_request_failure_409" })
                else dispatchDialogState({ type: "send_request_failure" })
            });
        }, 1000);

    }

    const handleAlert = () => {
        alert("rule_change_save_message")
    }

    const handleSaveClick = () => {
        if (criticalWarnings.length > 0) {
            setShowCriticalConfirm(true)
            return
        }
        handleFinish()
    }

    const handleConfirmCriticalSave = () => {
        setShowCriticalConfirm(false)
        handleFinish()
    }

    if (showCriticalConfirm) {
        return (
            <Dialog
                open={props.open}
                onClose={() => setShowCriticalConfirm(false)}
                keepMounted={false}
                disableBackdropClick
            >
                <DialogTitle style={{ paddingBottom: "0px" }} className={["blueText", "normalMediumSize"].join(" ")}>{t("Confirmação necessária")}</DialogTitle>
                <DialogContent>
                    <div style={{ width: "380px", paddingBottom: "20px" }}>
                        <div className="internalCardContainer" style={{ display: "flex", justifyContent: "center" }}>
                            <ClearIcon className="fraudStatusIcon" style={{ color: '#D91A1A', width: "40px", height: "40px" }} />
                        </div>
                        <div className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")} style={{ textAlign: "center" }}>
                            {criticalWarnings.length > 1
                                ? t("A análise de IA identificou problemas críticos neste trecho:")
                                : t("A análise de IA identificou um problema crítico neste trecho:")}
                        </div>
                        <div className="internalCardContainer">
                            {criticalWarnings.map((warning, index) => (
                                <div key={index} className="qia-warning-banner qia-warning-banner-critical">
                                    <div className="qia-warning-banner-title">{warning.title}</div>
                                    <div className="qia-warning-banner-text">{warning.description}</div>
                                </div>
                            ))}
                        </div>
                        <div className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")} style={{ textAlign: "center" }}>
                            {t("Salvar mesmo assim pode causar comportamento indevido em produção. Deseja continuar?")}
                        </div>
                        <div className="internalCardContainer" style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                            <div
                                className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                                style={{ width: "140px" }}
                                onClick={() => setShowCriticalConfirm(false)}
                            >
                                {t("Revisar trecho")}
                            </div>
                            <div
                                className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                style={{ width: "160px", backgroundColor: "#D91A1A" }}
                                onClick={handleConfirmCriticalSave}
                            >
                                {t("Salvar mesmo assim")}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        )
    }

    if (dialogState.finished && dialogState.isLoading) {
        return (
            <Dialog
                open={props.open}
                onClose={() => history.push("/rule-pieces-inbox")}
                keepMounted={false}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <DialogTitle style={{ paddingBottom: "0px" }} className={["blueText", "normalMediumSize"].join(" ")}>{t("Salvando Regra")}</DialogTitle>
                <DialogContent>
                    <div style={{ display: "flex", width: "310px", height: "217px" }}>
                        <CircularProgress style={{ margin: "auto" }} />
                    </div>
                </DialogContent>
            </Dialog>

        )
    }
    else if (dialogState.finished) {
        let confirmationData = {
            message: dialogState.message,
            success: !dialogState.isError
        }
        return (
            <Dialog
                open={props.open}
                onClose={confirmationData.success ? () => history.push("/rule-pieces-inbox") : handleCloseDialog}
                keepMounted={false}
                disableBackdropClick
            >
                <DialogTitle style={{ paddingBottom: "0px" }} className={["blueText", "normalMediumSize"].join(" ")}>{t("Confirmação de Revisão")}</DialogTitle>
                <DialogContent>
                    <div style={{ width: "310px", height: "217px" }}>
                        <div className="internalCardContainer" style={{ display: "flex", height: "40%", justifyContent: "center" }}>
                            {getConfirmationIcon(confirmationData.success)}
                        </div>
                        <div className="internalCardContainer" style={{ display: "flex", textAlign: "center", justifyContent: "center" }}>
                            {confirmationData.message}
                        </div>
                        <div className="internalCardContainer" style={{ display: "flex", justifyContent: "center" }}>
                            <div
                                className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                onClick={confirmationData.success ? () => history.push("/rule-pieces-inbox") : handleCloseDialog}
                            >
                                {confirmationData.success ? t("VOLTAR À TELA INICIAL") : "FECHAR"}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        )

    }
    return (
        <Dialog
            open={props.open}
            onClose={handleCloseDialog}
            keepMounted={false}
            maxWidth={agentEnabled ? "lg" : "sm"}
        >
            <DialogTitle style={{ paddingBottom: "0px" }} className={["blueText", "normalMediumSize"].join(" ")}>{t("Salvar Trecho")}</DialogTitle>
            <DialogContent style={{ paddingBottom: agentEnabled ? "24px" : "180px" }}>
                <div
                    style={{
                        width: agentEnabled ? "900px" : "366px",
                        height: agentEnabled ? "auto" : "160px",
                        maxWidth: "100%",
                        display: "flex", flexDirection: "column",
                        marginBottom: agentEnabled ? "10px" : "30px",
                        alignItems: "center"
                    }}
                >
                    <div
                        className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")}
                        style={{ width: "90%", textAlign: "center" }}>
                        {t("A versão corrente da regra será substituída pela árvore atual!")} <strong>{t("Deseja mesmo continuar?")}</strong>
                    </div>
                    {agentEnabled && agentWarnings.length > 0 &&
                        <div className="qia-warning-banners qia-fadeup" style={{ width: "90%", marginTop: "0px", marginBottom: "14px", boxSizing: "border-box" }}>
                            {agentWarnings.map((warning, index) => (
                                <div
                                    key={index}
                                    className={[
                                        "qia-warning-banner",
                                        warning.severity === "critical" ? "qia-warning-banner-critical" : "qia-warning-banner-attention",
                                        "qia-fadeup"
                                    ].join(" ")}
                                >
                                    <div className="qia-warning-banner-title">
                                        {(warning.severity === "critical" ? t("Problema crítico") : t("Atenção")) + ": " + warning.title}
                                    </div>
                                    <div className="qia-warning-banner-text">{warning.description}</div>
                                    {warning.suggestion &&
                                        <div className="qia-warning-banner-suggestion">
                                            {t("Sugestão") + ": " + warning.suggestion}
                                        </div>
                                    }
                                </div>
                            ))}
                        </div>
                    }
                    {agentEnabled &&
                        <div
                            className="internalCardContainer qia-ai-panel qia-fadeup"
                            style={{ width: "90%", textAlign: "left", boxSizing: "border-box" }}
                        >
                            <div className="qia-ai-panel-title">
                                <QiaAvatar size={20} />
                                <span className="qia-gradient-title">
                                    {t("Análise do trecho (IA)")}
                                </span>
                            </div>
                            {agentExplanationState.status === "loading" &&
                                <div className="qia-ai-loading">
                                    <QiaLoader
                                        label={t("Analisando o trecho...")}
                                        size={40}
                                    />
                                </div>
                            }
                            {(agentExplanationState.status === "streaming" || agentExplanationState.status === "success") &&
                                <div className="qia-save-analysis-scroll">
                                    <div className="qia-bot-bubble qia-fadeup">
                                        <CollapsibleMarkdown source={agentExplanationState.text} />
                                    </div>
                                </div>
                            }
                            {agentExplanationState.status === "error" &&
                                <div className="qia-ai-error qia-fadeup" style={{ textAlign: "center" }}>
                                    <div className={["normalText", "normalMediumSize"].join(" ")} style={{ marginBottom: "8px" }}>
                                        {t("Não foi possível gerar a explicação automática.")}
                                    </div>
                                    <div
                                        className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                                        style={{ display: "inline-block", padding: "4px 16px", width: "auto" }}
                                        onClick={runAgentExplanationFlow}
                                    >
                                        {t("Tentar novamente")}
                                    </div>
                                </div>
                            }
                        </div>
                    }
                    <div
                        className={["internalCardContainer", "normalText", "normalMediumSize"].join(" ")}
                        style={{ width: "90%" }}>
                        {t("Para continuar, inclua a descrição da alteração:")}
                    </div>
                    <div style={{ width: "90%", marginBottom: "15px" }}>
                        <TextField
                            error={!ruleDescription.length > 0}
                            label={t("Descrição da Alteração")}
                            style={{ textAlign: "center" }}
                            className="filterTextbox"
                            size="small" fullWidth
                            value={ruleDescription}
                            multiline
                            onChange={(e) => setRuleDescription(e.target.value)}
                            required
                        />
                    </div>
                    <div className="internalCardContainer" style={{ display: "flex", marginTop: "10px" }}>
                        <div
                            className={["button", "onlyboarder", "normalText", "normalMediumSize"].join(" ")}
                            style={{ width: "120px" }}
                            onClick={handleCloseDialog}
                        >
                            {t("Cancelar")}
                        </div>
                        {ruleDescription ?
                            <div
                                className={["button", "standard", "normalText", "normalMediumSize"].join(" ")}
                                style={{ width: "120px" }}
                                onClick={handleSaveClick}
                            >
                                {t("save")}
                            </div> :
                            <div
                                className={["button", "standard", "disabled", "normalText", "normalMediumSize"].join(" ")}
                                style={{ width: "120px" }}
                                onClick={handleAlert}

                            >
                                {t("save")}
                            </div>}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default SaveRulePieceDialog;
